import React, { useState } from 'react';
import styled from 'styled-components';

import { raisedSurfaceStyle } from 'components/Common/Card/Card';
import Tooltip from 'components/Tooltip';
import Button from 'kingpin/atoms/Button';
import ContextMenu, { Break, Option } from 'kingpin/atoms/ContextMenu';
import Icon from 'kingpin/atoms/Icon';
import Typography from 'kingpin/atoms/Typography';

import { NO_DRAG_CLASS } from './constants';
import MenuEventBoundary from './MenuEventBoundary';

// The header's padding, and the inset text-like bodies keep (Number values,
// Text blocks, Composite elements). Charts run to the block's edges.
export const SHELL_PADDING = { x: 12, y: 10 };
// A header's side padding, as a classic gadget's (CanvasCard), so a title
// fits wherever it did on the classic gadget
export const HEADER_PADDING_X = 8;

// The header's ⋮ menu, revealed while the block is hovered or selected
export const CARD_ACTIONS_CLASS = 'studio-card-actions';
// Keeps the menu's button revealed while the menu is open
export const CARD_ACTIONS_OPEN_CLASS = 'studio-card-actions-open';
// Room for the ⋮ button; the bar is only as wide as it
const ACTIONS_MAX_WIDTH = 32;

// The shell and header shared by every filled block (single-visual,
// Composite and Text). The whole shell is the drag handle. While text is
// being edited in it, it lets the formatting bubble overflow its edges.
// On a dashboard or report ($isEmbedded) the block sits inside the canvas's
// own card, which draws the border, and nothing drags it. Its menu buttons
// (the classic gadget ones) reveal on hover as they do in a classic card.
// Its action bar shows while it's hovered or selected ($isActive, marked with
// data-active for the bars floated over it to follow).
export const CardShell = styled.div.attrs<{ $isActive?: boolean }>((props) => ({
  'data-active': props.$isActive ? '' : undefined,
}))<{
  $isSelected: boolean;
  $isActive?: boolean;
  $isEditing?: boolean;
  $isEmbedded?: boolean;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  ${(props) => (props.$isEmbedded ? 'flex: 1; min-width: 0;' : '')}
  height: 100%;
  border-radius: ${(props) => (props.$isEmbedded ? 0 : 10)}px;
  overflow: ${(props) => (props.$isEditing ? 'visible' : 'hidden')};
  cursor: ${(props) => (props.$isEmbedded ? 'default' : 'grab')};
  background: var(--color-bg-raised);
  ${raisedSurfaceStyle};
  border: ${(props) =>
    props.$isEmbedded ? 'none' : '1px solid var(--color-border-tertiary)'};
  box-shadow: ${(props) =>
    props.$isSelected ? '0 0 0 2px var(--color-studio-selection)' : 'none'};
  &:active {
    cursor: ${(props) => (props.$isEmbedded ? 'default' : 'grabbing')};
  }

  .showOnMouseOver {
    opacity: 0;
    width: 0;
  }
  /*
   * Kept revealed while a picker opened from them is (its dates), as the
   * pointer leaves the block for the picker
   */
  &:hover .showOnMouseOver,
  &:has(.${CARD_ACTIONS_OPEN_CLASS}) .showOnMouseOver {
    opacity: 1;
    width: unset;
  }

  /*
   * Hidden, the bar takes no width, so the title can use the whole header.
   * Revealed on hover or selection (or keyboard focus), it pushes the
   * title's ellipsis in rather than overlapping it.
   */
  .${CARD_ACTIONS_CLASS} {
    max-width: 0;
    margin-left: 0;
    overflow: hidden;
    opacity: 0;
    transition:
      opacity 0.15s ease,
      max-width 0.15s ease,
      margin-left 0.15s ease;
  }

  &:hover
    .${CARD_ACTIONS_CLASS},
    &[data-active]
    .${CARD_ACTIONS_CLASS},
    .${CARD_ACTIONS_CLASS}:focus-within,
    .${CARD_ACTIONS_CLASS}.${CARD_ACTIONS_OPEN_CLASS} {
    max-width: ${ACTIONS_MAX_WIDTH}px;
    margin-left: 4px;
    opacity: 1;
  }
`;

const Header = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  min-height: 24px;
  padding: ${SHELL_PADDING.y}px ${HEADER_PADDING_X}px;
`;

const TitleColumn = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const Actions = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 4px;
`;

const HeaderIcon = styled.div`
  display: flex;
  flex-shrink: 0;
`;

const AccentDot = styled.div<{ $color: string }>`
  flex-shrink: 0;
  width: 10px;
  height: 10px;
  border-radius: 3px;
  background-color: ${(props) => props.$color};
`;

// The hover bar of a block without a header (Text, or a block with its
// header hidden): floated over the top-right corner on a raised surface.
// Hidden with the bar, so its shadow never frames an empty bar.
const OverlayBar = styled.div`
  position: absolute;
  top: 6px;
  right: 6px;
  z-index: 3;
  display: flex;
  padding: 2px;
  border-radius: 8px;
  background-color: var(--color-bg-primary);
  box-shadow: var(--shadow-dropdown);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease;

  ${CardShell}:hover &,
  ${CardShell}[data-active] &,
  &:focus-within,
  &:has(.${CARD_ACTIONS_OPEN_CLASS}) {
    opacity: 1;
    pointer-events: auto;
  }

  && .${CARD_ACTIONS_CLASS} {
    margin-left: 0;
  }
`;

// The hover menu's options. Blocks shown fullscreen get none: they're
// view-only there.
export interface CardActions {
  // Starred into the sidebar's Favourites. Without onToggleFavourite the
  // option's left out (see ARE_FAVOURITES_SHOWN)
  isFavourite: boolean;
  onToggleFavourite?: () => void;
  onFullscreen: () => void;
  onDuplicate: () => void;
  // Reveals that part of the block's settings, or of one of its Composite
  // elements' (useStudioState.focusSettings)
  onOpenSettings: (section: Studio.SettingsSection, elementId?: string) => void;
  // Opens the block's settings panel, as clicking the block does
  onEdit: () => void;
  onRemove: () => void;
  // False on a report, whose range the report sets
  hasDatesButton?: boolean;
}

// Quicker than a tooltip's usual second: the buttons are icons alone
const ACTION_TOOLTIP_DELAY_MS = 400;

// The hover icons are light until hovered. One tinted to show its setting is
// in use (iconColor) keeps its tint.
const ActionIcon = styled.span<{ $isTinted: boolean }>`
  display: flex;

  ${(props) =>
    !props.$isTinted &&
    `
    svg {
      color: var(--color-icon-secondary);
      transition: color 0.15s ease;
    }
    &:hover svg {
      color: var(--color-text-primary);
    }
  `}
`;

// A hover icon button (Classic blocks, empty blocks). Kingpin buttons take no
// tooltip, so it goes on a wrapper.
export const ActionButton = ({
  title,
  icon,
  iconColor,
  onClick,
  testId,
  ...rest
}: {
  title: string;
  icon: Kingpin.Icon;
  iconColor?: string;
  onClick: () => void;
  testId: string;
  'data-testid'?: string;
  'data-favourite'?: boolean;
}) => (
  <Tooltip
    content={title}
    position="top"
    delay={ACTION_TOOLTIP_DELAY_MS}
    isFlexAnchor
  >
    <ActionIcon $isTinted={!!iconColor} {...rest}>
      <Button
        type="Ghost"
        size="X-Small"
        icon={icon}
        iconColor={iconColor}
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        testId={testId}
      />
    </ActionIcon>
  </Tooltip>
);

interface ActionItem {
  key: string;
  title: string;
  icon: Kingpin.Icon;
  onClick: () => void;
  isDanger?: boolean;
}

const buildActionItems = ({
  actions,
  dates,
}: {
  actions: CardActions;
  dates?: { hasOwnDates: boolean; hasFilters: boolean };
}): ActionItem[] => [
  {
    key: 'edit',
    title: 'Edit',
    icon: 'edit-outline',
    onClick: actions.onEdit,
  },
  ...(actions.onToggleFavourite
    ? [
        {
          key: 'favourite',
          title: actions.isFavourite
            ? 'Remove from favourites'
            : 'Add to favourites',
          icon: actions.isFavourite
            ? ('start - filled' as const)
            : ('star - empty' as const),
          onClick: actions.onToggleFavourite,
        },
      ]
    : []),
  ...(dates && actions.hasDatesButton !== false
    ? [
        {
          key: 'dates',
          title: dates.hasOwnDates ? 'Date range (own)' : 'Date range',
          icon: 'calendar-outline' as const,
          onClick: () => actions.onOpenSettings('dates'),
        },
      ]
    : []),
  ...(dates
    ? [
        {
          key: 'filters',
          title: dates.hasFilters ? 'Filters (applied)' : 'Filters',
          icon: 'filters-outline' as const,
          onClick: () => actions.onOpenSettings('filters'),
        },
      ]
    : []),
  {
    key: 'fullscreen',
    title: 'Fullscreen',
    icon: 'fullscreen-outline',
    onClick: actions.onFullscreen,
  },
  {
    key: 'duplicate',
    title: 'Duplicate',
    icon: 'copy-outline',
    onClick: actions.onDuplicate,
  },
  {
    key: 'remove',
    title: 'Remove',
    icon: 'cross-outline',
    onClick: actions.onRemove,
    isDanger: true,
  },
];

// Edit, favourite, dates, filters, fullscreen, duplicate and remove, in one
// ⋮ menu. Blocks without dates or filters of their own (Text) leave those two
// out. A block's own options (extraMenu, e.g. a Table's export) sit before
// Remove. The button stays revealed while the menu is open, as the pointer
// leaves the block for it.
export const CardActionButtons = ({
  cardId,
  actions,
  dates,
  extraMenu,
}: {
  cardId: string;
  actions: CardActions;
  // Labels the date / filter options when the block has its own
  dates?: { hasOwnDates: boolean; hasFilters: boolean };
  // The block's own menu options (see BlockMenuItems)
  extraMenu?: JSX.Element;
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const items = buildActionItems({ actions, dates });

  return (
    <Actions
      className={[
        NO_DRAG_CLASS,
        CARD_ACTIONS_CLASS,
        isMenuOpen ? CARD_ACTIONS_OPEN_CLASS : '',
      ].join(' ')}
      // Keep the press from selecting the block first (a Composite header
      // selects on mousedown), which opens the settings panel and reflows the
      // canvas before the click lands
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <ActionIcon $isTinted={false}>
        <ContextMenu
          icon="kebab-outline"
          buttonType="Ghost"
          tooltip="Actions"
          testId={`studio-card-menu-${cardId}`}
          onOpen={() => setIsMenuOpen(true)}
          onClose={() => setIsMenuOpen(false)}
        >
          <MenuEventBoundary>
            {items.map((item) => (
              <React.Fragment key={item.key}>
                {item.isDanger && extraMenu && (
                  <>
                    <Break />
                    {extraMenu}
                  </>
                )}
                {item.isDanger && <Break />}
                <Option
                  label={item.title}
                  icon={item.icon}
                  isDanger={item.isDanger}
                  onClick={item.onClick}
                  testId={`studio-card-${item.key}-${cardId}`}
                />
              </React.Fragment>
            ))}
          </MenuEventBoundary>
        </ContextMenu>
      </ActionIcon>
    </Actions>
  );
};

export const CardHeader = ({
  cardId,
  title,
  description,
  accentColor,
  icon,
  hasOwnDates,
  hasFilters,
  actions,
  viewMenu,
  extraMenu,
}: {
  cardId: string;
  title: string;
  description?: string;
  // Composite blocks lead their title with a coloured dot
  accentColor?: string;
  // Leads the title instead of the dot
  icon?: { name: Kingpin.Icon; color: string };
  hasOwnDates: boolean;
  hasFilters: boolean;
  // Absent in fullscreen
  actions?: CardActions;
  // On a dashboard or report, where there are no actions: its menu buttons
  viewMenu?: JSX.Element;
  // The block's own menu options, in Studio
  extraMenu?: JSX.Element;
}) => (
  <Header data-testid={`studio-card-header-${cardId}`}>
    <TitleColumn>
      <TitleRow>
        {icon ? (
          <HeaderIcon data-testid={`studio-card-icon-${cardId}`}>
            <Icon icon={icon.name} color={icon.color} width={16} height={16} />
          </HeaderIcon>
        ) : (
          accentColor && (
            <AccentDot
              $color={accentColor}
              data-testid={`studio-card-accent-${cardId}`}
            />
          )
        )}
        {/* Set as a classic gadget's title (CanvasCardTitle) */}
        <Typography.Header type="H6" isEllipsis>
          {title}
        </Typography.Header>
      </TitleRow>
      {description && (
        <Typography.Body
          type="Body 12"
          color="var(--color-text-tertiary)"
          isEllipsis
        >
          {description}
        </Typography.Body>
      )}
    </TitleColumn>
    {actions && (
      <CardActionButtons
        cardId={cardId}
        actions={actions}
        dates={{ hasOwnDates, hasFilters }}
        extraMenu={extraMenu}
      />
    )}
    {!actions && viewMenu}
  </Header>
);

export const OverlayActions = ({
  cardId,
  actions,
  dates,
  extraMenu,
}: {
  cardId: string;
  actions: CardActions;
  // Text has no dates or filters of its own
  dates?: { hasOwnDates: boolean; hasFilters: boolean };
  // The block's own menu options
  extraMenu?: JSX.Element;
}) => (
  <OverlayBar data-testid={`studio-card-overlay-actions-${cardId}`}>
    <CardActionButtons
      cardId={cardId}
      actions={actions}
      dates={dates}
      extraMenu={extraMenu}
    />
  </OverlayBar>
);

// A dashboard or report block's menu buttons, floated over its corner when
// its header is hidden
export const OverlayViewMenu = ({
  cardId,
  children,
}: {
  cardId: string;
  children: JSX.Element;
}) => (
  <OverlayBar data-testid={`studio-card-overlay-menu-${cardId}`}>
    {children}
  </OverlayBar>
);
